// ==================== AUTH SERVICE ====================

import { apiService } from './api';
import { APP_CONSTANTS } from '../constants';
import { websocketService } from './websocket.service';
import type { User, AuthTokens, LoginCredentials, RegisterData, ApiResponse } from '../types';

// ==================== AUTH RESPONSE TYPES ====================

export interface LoginResponse {
  user: User;
  tokens: AuthTokens;
}

export interface RegisterResponse {
  user: User;
  message: string;
}

// MeResponse - the /auth/me endpoint returns user directly in data
export type MeResponse = User;

// ==================== AUTH SERVICE ====================

export const authService = {
  /**
   * Login with email and password
   */
  login: async (credentials: LoginCredentials): Promise<ApiResponse<LoginResponse>> => {
    const response = await apiService.post<LoginResponse>('/auth/login', credentials);
    
    if (response.success && response.data) {
      // Store tokens and user in localStorage
      const { tokens, user } = response.data;
      localStorage.setItem(APP_CONSTANTS.STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
      localStorage.setItem(APP_CONSTANTS.STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
      localStorage.setItem(APP_CONSTANTS.STORAGE_KEYS.USER, JSON.stringify(user));
    }
    
    return response;
  },

  /**
   * Register a new customer account
   */
  register: async (data: RegisterData): Promise<ApiResponse<RegisterResponse>> => {
    return apiService.post<RegisterResponse>('/auth/register', data);
  },

  /**
   * Logout - clear tokens and user data, disconnect WebSocket
   */
  logout: (): void => {
    // Disconnect WebSocket before clearing tokens
    websocketService.disconnect();
    
    localStorage.removeItem(APP_CONSTANTS.STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(APP_CONSTANTS.STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(APP_CONSTANTS.STORAGE_KEYS.USER);
  },

  /**
   * Get current user from token
   */
  getCurrentUser: async (): Promise<ApiResponse<MeResponse>> => {
    return apiService.get<MeResponse>('/auth/me');
  },

  /**
   * Refresh access token
   */
  refreshToken: async (refreshToken: string): Promise<ApiResponse<AuthTokens>> => {
    return apiService.post<AuthTokens>('/auth/refresh', { refreshToken });
  },

  /**
   * Request password reset
   */
  forgotPassword: async (email: string): Promise<ApiResponse<{ message: string }>> => {
    return apiService.post<{ message: string }>('/auth/forgot-password', { email });
  },

  /**
   * Reset password with OTP
   */
  resetPassword: async (email: string, otp: string, newPassword: string): Promise<ApiResponse<{ success: boolean }>> => {
    return apiService.post<{ success: boolean }>('/auth/reset-password', { email, otp, newPassword });
  },

  /**
   * Verify password reset OTP (without consuming it)
   */
  verifyPasswordResetOtp: async (email: string, otp: string): Promise<ApiResponse<{ valid: boolean }>> => {
    return apiService.post<{ valid: boolean }>('/auth/verify-password-reset-otp', { email, otp });
  },

  /**
   * Resend password reset OTP
   */
  resendPasswordResetOtp: async (email: string): Promise<ApiResponse<{ sent: boolean }>> => {
    return apiService.post<{ sent: boolean }>('/auth/resend-otp', { 
      email, 
      type: 'PASSWORD_RESET' 
    });
  },

  /**
   * Verify email with OTP
   */
  verifyEmail: async (email: string, otp: string): Promise<ApiResponse<{ verified: boolean }>> => {
    return apiService.post<{ verified: boolean }>('/auth/verify-email', { email, otp });
  },

  /**
   * Resend verification OTP
   */
  resendVerificationOtp: async (email: string): Promise<ApiResponse<{ sent: boolean }>> => {
    return apiService.post<{ sent: boolean }>('/auth/resend-otp', { 
      email, 
      type: 'EMAIL_VERIFICATION' 
    });
  },

  /**
   * Check if user is authenticated (has valid token)
   */
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.ACCESS_TOKEN);
    return !!token;
  },

  /**
   * Get stored user from localStorage
   */
  getStoredUser: (): User | null => {
    const userStr = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.USER);
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr) as User;
    } catch {
      return null;
    }
  },

  /**
   * Get stored tokens from localStorage
   */
  getStoredTokens: (): AuthTokens | null => {
    const accessToken = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.ACCESS_TOKEN);
    const refreshToken = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.REFRESH_TOKEN);
    
    if (!accessToken || !refreshToken) return null;
    
    return { accessToken, refreshToken };
  },

  /**
   * Update stored user data
   */
  updateStoredUser: (user: User): void => {
    localStorage.setItem(APP_CONSTANTS.STORAGE_KEYS.USER, JSON.stringify(user));
  },

  /**
   * Update user profile (first name, last name)
   */
  updateProfile: async (data: { firstName?: string; lastName?: string }): Promise<ApiResponse<User>> => {
    const response = await apiService.patch<User>('/auth/profile', data);
    
    if (response.success && response.data) {
      // Update stored user in localStorage
      localStorage.setItem(APP_CONSTANTS.STORAGE_KEYS.USER, JSON.stringify(response.data));
    }
    
    return response;
  },

  /**
   * Change user password
   */
  changePassword: async (data: { currentPassword: string; newPassword: string }): Promise<ApiResponse<{ success: boolean }>> => {
    return apiService.post<{ success: boolean }>('/auth/change-password', data);
  },

  /**
   * Update organization information
   */
  updateOrganization: async (data: {
    businessName?: string;
    businessEmail?: string;
    businessPhone?: string;
    businessAddress?: string;
    businessWebsite?: string;
    industry?: string;
  }): Promise<ApiResponse<User>> => {
    const response = await apiService.patch<User>('/auth/organization', data);
    
    if (response.success && response.data) {
      // Update stored user in localStorage
      localStorage.setItem(APP_CONSTANTS.STORAGE_KEYS.USER, JSON.stringify(response.data));
    }
    
    return response;
  },
};

export default authService;

